import React, { useState, useEffect, useMemo } from "react"
import { useSearchParams, useNavigate } from "react-router"
import { Loader2, Rocket, Sparkles, Target, PlayCircle, BookOpen, Briefcase, GraduationCap, ExternalLink, ArrowRight } from "lucide-react"
import { Sidebar } from "../components/ui/sidebar"
import { CareerHeader } from "../components/ui/career-learning-path/career-learning-header"
import { CareerTabs } from "../components/ui/career-learning-path/career-tabs"
import { LearningJourney } from "../components/ui/career-learning-path/learning-journey"
import { SkillTracker } from "../components/ui/career-learning-path/skill-tracker"
import { CourseProgressJourney } from "../components/ui/career-learning-path/course-progress-journey"
import { CurriculumTracker } from "../components/ui/career-learning-path/curriculum-tracker"
import { CareerSidebarCard } from "../components/ui/career-learning-path/career-sidebar-card"
import { UniversalSearch } from "../components/ui/universal-search"
import ProtectedRoute from "../components/ProtectedRoute"
import { careerPathApi } from "@/hooks/api/careerPathApi"
import { AuthStore } from "@/hooks/stores/AuthStore"
import { StorageService } from "@/hooks/services/storageService"
import { useCareerOverview } from "@/hooks/useCareerOverview"
import { getAllNodes } from "@/data/career-tree"

// ─── Maps CareerPathContent → LearningJourney grade-level format ─────────────
// Groups modules by rough "stage" so LearningJourney renders something meaningful
function buildGradeLevels(modules) {
  if (!modules?.length) return []
  const chunkSize = Math.ceil(modules.length / 3)
  const stages = ["Foundation", "Intermediate", "Advanced"]
  return stages.map((stage, i) => {
    const slice = modules.slice(i * chunkSize, (i + 1) * chunkSize)
    return {
      level: stage,
      subjects: slice.map((m) => ({
        name: m.title,
        type: i === 0 ? "Compulsory" : "Optional",
      })),
    }
  }).filter((s) => s.subjects.length > 0)
}

// Maps required traits to SkillTracker format using a short name lookup
const TRAIT_NAMES = {
  T01: "Critical Thinking",  T02: "Analytical Reasoning", T03: "Creativity",
  T04: "Numerical Ability",  T05: "Problem Solving",       T06: "Technical Aptitude",
  T07: "Entrepreneurship",   T09: "Strategic Planning",    T14: "Empathy",
  T17: "Communication",      T22: "Writing",               T23: "Emotional Intelligence",
}

function buildSkills(requiredTraits) {
  if (!requiredTraits?.length) return [
    { name: "Critical Thinking",  percentage: 60 },
    { name: "Communication",      percentage: 70 },
    { name: "Problem Solving",    percentage: 65 },
    { name: "Adaptability",       percentage: 75 },
  ]
  return requiredTraits.map((rt) => ({
    name: TRAIT_NAMES[rt.traitId] ?? rt.traitId,
    percentage: rt.threshold,
  }))
}

// Maps modules + journey record → CurriculumTracker items
function buildCurriculumItems(modules, completedIds) {
  return modules.map((m) => {
    const done    = completedIds.includes(m.id)
    const current = !done && !m.isLocked
    return {
      id:       m.id,
      name:     m.title,
      subject:  m.description?.split(" ").slice(0, 4).join(" ") ?? "",
      progress: done ? 100 : current ? 30 : 0,
      status:   done ? "completed" : m.isLocked ? "locked" : current ? "in-progress" : "upcoming",
    }
  })
}

// Maps journey record → CourseProgressJourney milestones
function buildMilestones(modules, completedIds) {
  return modules.map((m, i) => ({
    id:        i + 1,
    label:     m.title,
    completed: completedIds.includes(m.id),
    current:   !completedIds.includes(m.id) && (i === 0 || completedIds.includes(modules[i - 1]?.id)),
  }))
}

// ─── Career Overview Panel ───────────────────────────────────────────────────

// Maps field display names (from career overview `group`) to tree nodeIds.
// Lazily built once from getAllNodes().
const FIELD_NAME_TO_NODE_ID = (() => {
  const map = {}
  for (const node of getAllNodes()) {
    if (node.level === "field") map[node.name] = node.nodeId
  }
  return map
})()

function CareerOverviewCard({ career, navigate }) {
  const fieldNodeId = FIELD_NAME_TO_NODE_ID[career.group]
  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 flex flex-col gap-4">
      {/* Card header */}
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center shrink-0">
          <Briefcase className="w-5 h-5 text-indigo-500" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-bold text-slate-800 leading-snug">{career.careerName}</h4>
          <span className="text-[11px] text-indigo-500 font-medium">{career.group}</span>
        </div>
        {fieldNodeId && navigate && (
          <button
            onClick={() => navigate("/broad-path-selection?mode=add", { state: { preselectNodeId: fieldNodeId } })}
            className="shrink-0 flex items-center gap-1 px-2.5 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 text-[11px] font-semibold rounded-lg transition-all active:scale-95"
          >
            Add path
            <ArrowRight className="w-3 h-3" />
          </button>
        )}
      </div>

      {/* Overview */}
      {career.overview && (
        <p className="text-[13px] text-slate-600 leading-relaxed">{career.overview}</p>
      )}

      {/* Traits */}
      {career.traits?.length > 0 && (
        <div>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Key Traits</p>
          <div className="flex flex-wrap gap-1.5">
            {career.traits.map((t) => (
              <span key={t} className="px-2.5 py-1 bg-indigo-50 text-indigo-700 rounded-full text-[11px] font-medium">{t}</span>
            ))}
          </div>
        </div>
      )}

      {/* Day-to-day */}
      {career.dayToDay?.length > 0 && (
        <div>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Day-to-Day</p>
          <ul className="space-y-1.5">
            {career.dayToDay.slice(0, 4).map((item, i) => (
              <li key={i} className="text-[12px] text-slate-600 flex gap-2">
                <span className="text-indigo-400 shrink-0 mt-0.5">•</span>
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Challenges */}
      {career.drawbacks?.length > 0 && (
        <div>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Challenges</p>
          <ul className="space-y-1.5">
            {career.drawbacks.slice(0, 3).map((item, i) => (
              <li key={i} className="text-[12px] text-slate-600 flex gap-2">
                <span className="text-amber-400 shrink-0 mt-0.5">•</span>
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Academic requirements */}
      {career.requirements?.academic?.length > 0 && (
        <div>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <GraduationCap className="w-3 h-3" />
            Academic Requirements
          </p>
          <ul className="space-y-1.5">
            {career.requirements.academic.map((item, i) => (
              <li key={i} className="text-[12px] text-slate-600 flex gap-2">
                <span className="text-teal-400 shrink-0 mt-0.5">•</span>
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Academic notes */}
      {career.requirements?.academicNotes?.length > 0 && (
        <div>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Study Path</p>
          <ul className="space-y-1.5">
            {career.requirements.academicNotes.map((item, i) => (
              <li key={i} className="text-[12px] text-slate-600 flex gap-2">
                <span className="text-violet-400 shrink-0 mt-0.5">•</span>
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Training notes */}
      {career.requirements?.trainingNotes?.length > 0 && (
        <div>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Training & Certifications</p>
          <ul className="space-y-1.5">
            {career.requirements.trainingNotes.map((item, i) => (
              <li key={i} className="text-[12px] text-slate-600 flex gap-2">
                <span className="text-sky-400 shrink-0 mt-0.5">•</span>
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Routes */}
      {(career.routes?.academic?.length > 0 || career.routes?.vocation?.length > 0 || career.routes?.workBased_training?.length > 0) && (
        <div>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Entry Routes</p>
          <div className="space-y-2">
            {career.routes.academic?.length > 0 && (
              <div>
                <span className="text-[10px] font-semibold text-indigo-500 uppercase tracking-wide">Academic</span>
                <ul className="mt-1 space-y-1">
                  {career.routes.academic.map((item, i) => (
                    <li key={i} className="text-[12px] text-slate-600 flex gap-2">
                      <span className="text-indigo-300 shrink-0 mt-0.5">•</span>{item}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {career.routes.vocation?.length > 0 && (
              <div>
                <span className="text-[10px] font-semibold text-emerald-500 uppercase tracking-wide">Vocational</span>
                <ul className="mt-1 space-y-1">
                  {career.routes.vocation.map((item, i) => (
                    <li key={i} className="text-[12px] text-slate-600 flex gap-2">
                      <span className="text-emerald-300 shrink-0 mt-0.5">•</span>{item}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {career.routes.workBased_training?.length > 0 && (
              <div>
                <span className="text-[10px] font-semibold text-amber-500 uppercase tracking-wide">Work-Based</span>
                <ul className="mt-1 space-y-1">
                  {career.routes.workBased_training.map((item, i) => (
                    <li key={i} className="text-[12px] text-slate-600 flex gap-2">
                      <span className="text-amber-300 shrink-0 mt-0.5">•</span>{item}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Related job titles */}
      {career.resources?.jobs?.length > 0 && (
        <div>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Job Titles</p>
          <div className="flex flex-wrap gap-1.5">
            {career.resources.jobs.map((job, i) => (
              <span key={i} className="px-2.5 py-1 bg-slate-100 text-slate-600 rounded-full text-[11px] font-medium">{job}</span>
            ))}
          </div>
        </div>
      )}

      {/* Links */}
      {(career.resources?.keyLinks?.length > 0 || career.usefulVideos?.length > 0) && (
        <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
          {career.resources?.keyLinks?.slice(0, 3).map((link, i) => (
            <a key={i} href={link} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-2.5 py-1 bg-white border border-slate-200 rounded-lg text-[11px] text-indigo-600 hover:bg-indigo-50 hover:border-indigo-200 transition-colors font-medium">
              <ExternalLink className="w-3 h-3" />
              Resource {i + 1}
            </a>
          ))}
          {career.usefulVideos?.slice(0, 2).map((link, i) => (
            <a key={i} href={link} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-2.5 py-1 bg-white border border-slate-200 rounded-lg text-[11px] text-rose-600 hover:bg-rose-50 hover:border-rose-200 transition-colors font-medium">
              <PlayCircle className="w-3 h-3" />
              Watch
            </a>
          ))}
        </div>
      )}
    </div>
  )
}

function CareerOverviewPanel({ overviews, isLoading, isBroadPath, search, onSearchChange, navigate }) {
  const filtered = overviews.filter((o) => {
    if (!search) return true
    const q = search.toLowerCase()
    return o.careerName.toLowerCase().includes(q) || o.group.toLowerCase().includes(q)
  })

  if (isLoading) {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-8 flex items-center justify-center gap-3 text-slate-400">
        <Loader2 className="w-5 h-5 animate-spin text-indigo-500" />
        <span className="text-sm font-medium">Loading career overviews…</span>
      </div>
    )
  }

  if (!overviews.length) {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-8 text-center">
        <BookOpen className="w-8 h-8 text-slate-300 mx-auto mb-2" />
        <p className="text-sm font-medium text-slate-500">No career overviews available for this path yet.</p>
      </div>
    )
  }

  // ── Non-broad path: show only the matching career(s) directly — no grouping ──
  if (!isBroadPath) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
          {filtered.map((career) => (
            <CareerOverviewCard key={career.slug} career={career} navigate={navigate} />
          ))}
        </div>
      </div>
    )
  }

  // ── Broad path: grouped listing with search ────────────────────────────────
  const groupedByField = filtered.reduce((acc, o) => {
    if (!acc[o.group]) acc[o.group] = []
    acc[o.group].push(o)
    return acc
  }, {})

  return (
    <div className="space-y-6">
      {/* Search */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm px-4 py-3 flex items-center gap-3">
        <BookOpen className="w-4 h-4 text-slate-400 shrink-0" />
        <input
          type="text"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search careers or fields…"
          className="flex-1 text-sm bg-transparent outline-none text-slate-700 placeholder-slate-400"
        />
        {search && (
          <button onClick={() => onSearchChange("")} className="text-xs text-slate-400 hover:text-slate-600">
            Clear
          </button>
        )}
        <span className="text-xs text-slate-400 shrink-0">{filtered.length} career{filtered.length !== 1 ? "s" : ""}</span>
      </div>

      {/* Grouped career cards */}
      {Object.entries(groupedByField).map(([group, careers]) => (
        <div key={group} className="space-y-3">
          <div className="flex items-center gap-3">
            <h3 className="text-sm font-bold text-slate-700 shrink-0">{group}</h3>
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-[11px] text-slate-400 shrink-0">{careers.length} career{careers.length !== 1 ? "s" : ""}</span>
          </div>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
            {careers.map((career) => (
              <CareerOverviewCard key={career.slug} career={career} navigate={navigate} />
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

// ----------------------------
// PAGE COMPONENT
// ----------------------------
export default function CareerLearningPathPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const pathId = searchParams.get("id") ?? "broad_stem_tech"

  const [activeTab, setActiveTab] = useState("Explore Path")
  const [pathContent, setPathContent] = useState(null)
  const [journeyRecord, setJourneyRecord] = useState(null)
  const [isPathSelected, setIsPathSelected] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [overviewSearch, setOverviewSearch] = useState("")

  const { overviews, isLoading: overviewsLoading, isBroadPath } = useCareerOverview(pathId)

  const handleBack = () => window.history.back()
  const handleSearch = (query) => console.log("Search:", query)

  useEffect(() => {
    let cancelled = false
    async function load() {
      setIsLoading(true)
      try {
        const user = AuthStore.getState().currentUser
        const [content, record] = await Promise.all([
          careerPathApi.getContent(pathId),
          user ? careerPathApi.getJourney(user._id, pathId) : Promise.resolve(null),
        ])
        if (cancelled) return
        setPathContent(content)
        setJourneyRecord(record)

        // Check if path is in student's selected paths
        if (user) {
          const students = StorageService.read("students") ?? []
          const student = students.find(
            (s) => s.userId === user._id || s.email === user.email
          )
          const selected = student?.selectedBroadPaths ?? []
          setIsPathSelected(selected.includes(pathId))
        }
      } catch (err) {
        console.error("[CareerLearningPath] load error:", err)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [pathId])

  if (isLoading || !pathContent) {
    return (
      <ProtectedRoute>
        <div className="flex min-h-screen bg-slate-100">
          <Sidebar />
          <main className="flex-1 lg:ml-64 flex items-center justify-center">
            <div className="flex flex-col items-center gap-3 text-slate-400">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
              <p className="text-sm font-medium">Loading career path…</p>
            </div>
          </main>
        </div>
      </ProtectedRoute>
    )
  }

  const completedIds   = journeyRecord?.completedModuleIds ?? []
  const gradeLevels    = buildGradeLevels(pathContent.modules)
  const skills         = buildSkills(pathContent.requiredTraits)
  const curriculumItems = buildCurriculumItems(pathContent.modules, completedIds)
  const milestones     = buildMilestones(pathContent.modules, completedIds)

  // Build sidebar card props from path content
  const sidebarData = {
    salaryData: pathContent.salaryData,
    foundationData: pathContent.foundationData,
    industryInsights: pathContent.industryInsights,
    certifications: pathContent.certifications,
  }

  return (
    <ProtectedRoute>
      <div className="flex min-h-screen bg-slate-100">
        <Sidebar />
        <UniversalSearch onSearch={handleSearch} />
        

        <main className="flex-1 lg:ml-64 p-4 md:p-6 overflow-y-auto">
          <div className="max-w-6xl mx-auto space-y-4">
            <CareerHeader
              careerName={pathContent.name}
              description={pathContent.description}
              matchPercentage={pathContent.matchPercentage ?? 80}
              category={pathContent.category}
              imageUrl={pathContent.imageUrl}
              onBack={handleBack}
            />

            {/* Journey CTA strip — hidden until video content is ready */}

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
              {/* LEFT CONTENT */}
              <div className="lg:col-span-3 space-y-4">
                <CareerTabs activeTab={activeTab} onTabChange={setActiveTab} />

                {activeTab === "Explore Path" && (
                  <div className="space-y-4">
                    <LearningJourney gradeLevels={gradeLevels} />
                    <SkillTracker skills={skills} />
                    <CareerOverviewPanel
                      overviews={overviews}
                      isLoading={overviewsLoading}
                      isBroadPath={isBroadPath}
                      search={overviewSearch}
                      onSearchChange={setOverviewSearch}
                      navigate={navigate}
                    />
                  </div>
                )}

                {activeTab === "My Learning Journey" && (
                  <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-4 space-y-6">
                    <CourseProgressJourney milestones={milestones} />
                    <CurriculumTracker items={curriculumItems} />
                  </div>
                )}
              </div>

              {/* RIGHT SIDEBAR */}
              <div className="lg:col-span-1">
                <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-4 sticky top-4">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Quick Access</p>
                  <CareerSidebarCard careerData={sidebarData} />
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </ProtectedRoute>
  )
}
